/**
 * BuyerIntelligence Component
 * Displays buyer acquisition signals, pipeline gaps, and M&A intelligence
 */

import { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Zap,
  DollarSign,
  Building2,
  Calendar,
  Target,
  Shield,
  Clock,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Info,
  Flame,
  Activity,
  Briefcase,
  BarChart3,
  Lightbulb,
  PieChart
} from 'lucide-react';
import { buyersAPI, matchingAPI } from '../services/api';

// Signal type configurations - Comprehensive BD Intelligence (85 types)
const SIGNAL_TYPE_CONFIG = {
  // PIPELINE / R&D SIGNALS
  pipeline_failure: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Pipeline Failure' },
  pipeline_success: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'Pipeline Success' },
  pipeline_phase_advance: { icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'Phase Advance' },
  clinical_trial_start: { icon: Activity, color: 'text-sky-600', bg: 'bg-sky-50', border: 'border-sky-200', label: 'Trial Start' },
  clinical_trial_suspension: { icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Trial Suspension' },
  clinical_hold: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Clinical Hold' },
  fda_approval: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'FDA Approval' },
  fda_decision: { icon: Shield, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'FDA Decision' },
  breakthrough_designation: { icon: Zap, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', label: 'Breakthrough Therapy' },
  fast_track_designation: { icon: Zap, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200', label: 'Fast Track' },
  priority_review: { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Priority Review' },
  orphan_drug_designation: { icon: Target, color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200', label: 'Orphan Drug' },
  ema_approval: { icon: CheckCircle2, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'EMA Approval' },
  nmpa_approval: { icon: CheckCircle2, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'NMPA Approval' },
  real_world_evidence: { icon: BarChart3, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', label: 'RWE Data' },

  // BD DEAL SIGNALS
  acquisition_announced: { icon: Building2, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Acquisition Announced' },
  acquisition_rumor: { icon: Activity, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', label: 'Acquisition Rumor' },
  acquisition_completed: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'Acquisition Completed' },
  acquisition_terminated: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Acquisition Terminated' },
  partnership: { icon: Building2, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Partnership' },
  licensing_in: { icon: Building2, color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200', label: 'Licensing-In' },
  licensing_out: { icon: Building2, color: 'text-cyan-600', bg: 'bg-cyan-50', border: 'border-cyan-200', label: 'Licensing-Out' },
  option_exercise: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'Option Exercise' },
  milestone_payment: { icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Milestone Payment' },
  divestiture: { icon: TrendingDown, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Divestiture' },
  spin_off: { icon: Building2, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200', label: 'Spin-Off' },
  competitor_deal: { icon: Shield, color: 'text-pink-600', bg: 'bg-pink-50', border: 'border-pink-200', label: 'Competitor Deal' },
  china_bd_activity: { icon: Building2, color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-200', label: 'China BD' },
  geographic_expansion: { icon: Target, color: 'text-teal-600', bg: 'bg-teal-50', border: 'border-teal-200', label: 'Geo Expansion' },

  // FINANCIAL / STRATEGIC SIGNALS
  patent_cliff: { icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Patent Cliff' },
  patent_expiry: { icon: Clock, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Patent Expiry' },
  ip_litigation: { icon: Shield, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'IP Litigation' },
  earnings_call: { icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Earnings Call' },
  earnings_mention: { icon: DollarSign, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', label: 'BD Mention' },
  earnings_beat: { icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'Earnings Beat' },
  earnings_miss: { icon: TrendingDown, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Earnings Miss' },
  revenue_guidance_change: { icon: BarChart3, color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200', label: 'Guidance Change' },
  financial_event: { icon: DollarSign, color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200', label: 'Financial Event' },
  debt_issuance: { icon: DollarSign, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', label: 'Debt Issuance' },
  equity_offering: { icon: DollarSign, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Equity Offering' },
  stock_buyback: { icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'Stock Buyback' },
  dividend_change: { icon: DollarSign, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', label: 'Dividend Change' },
  strategy_shift: { icon: Target, color: 'text-cyan-600', bg: 'bg-cyan-50', border: 'border-cyan-200', label: 'Strategy Shift' },
  cost_cutting: { icon: TrendingDown, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Cost Cutting' },

  // ORGANIZATIONAL SIGNALS
  exec_change: { icon: Briefcase, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200', label: 'Exec Change' },
  ceo_change: { icon: Briefcase, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', label: 'CEO Change' },
  cfo_change: { icon: Briefcase, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'CFO Change' },
  cso_change: { icon: Briefcase, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'CSO Change' },
  cbo_change: { icon: Briefcase, color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200', label: 'CBO Change' },
  board_change: { icon: Briefcase, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', label: 'Board Change' },
  layoff: { icon: TrendingDown, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Layoff' },
  restructuring: { icon: Activity, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Restructuring' },
  site_closure: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Site Closure' },
  hiring_surge: { icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'Hiring Surge' },

  // MARKET / COMPETITIVE SIGNALS
  competitor_move: { icon: Shield, color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-200', label: 'Competitor Move' },
  competitor_approval: { icon: Shield, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Competitor Approval' },
  competitor_failure: { icon: Shield, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'Competitor Failure' },
  market_share_change: { icon: BarChart3, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', label: 'Market Share' },
  pricing_pressure: { icon: TrendingDown, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Pricing Pressure' },
  regulatory_event: { icon: Shield, color: 'text-teal-600', bg: 'bg-teal-50', border: 'border-teal-200', label: 'Regulatory Event' },
  regulatory_warning: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Regulatory Warning' },
  market_event: { icon: Activity, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', label: 'Market Event' },
  payer_coverage: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'Payer Coverage' },
  generic_entry: { icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Generic Entry' },

  // MANUFACTURING / SUPPLY SIGNALS
  manufacturing_expansion: { icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'Mfg Expansion' },
  cmo_partnership: { icon: Building2, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'CMO Partnership' },
  supply_disruption: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Supply Disruption' },
  quality_issue: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Quality Issue' },

  // INVESTOR / ANALYST SIGNALS
  sec_filing: { icon: Info, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', label: 'SEC Filing' },
  activist_investor: { icon: AlertTriangle, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', label: 'Activist Investor' },
  analyst_upgrade: { icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'Analyst Upgrade' },
  analyst_downgrade: { icon: TrendingDown, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Analyst Downgrade' },
  insider_buying: { icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'Insider Buying' },
  insider_selling: { icon: TrendingDown, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Insider Selling' },
  institutional_accumulation: { icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Inst. Accumulation' },
  short_interest_change: { icon: Activity, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', label: 'Short Interest' },

  // CONFERENCE / KOL SIGNALS
  conference: { icon: Calendar, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200', label: 'Conference' },
  conference_attendance: { icon: Calendar, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200', label: 'Conference Attendance' },
  conference_presentation: { icon: Calendar, color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200', label: 'Presentation' },
  kol_engagement: { icon: Briefcase, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', label: 'KOL Engagement' },
  investor_day: { icon: Calendar, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Investor Day' },
  rd_day: { icon: Calendar, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'R&D Day' },

  // MISC SIGNALS
  manual_note: { icon: Info, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', label: 'BD Note' },
  news_mention: { icon: Info, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', label: 'News Mention' },
  social_sentiment: { icon: Activity, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', label: 'Social Sentiment' },
  litigation: { icon: Shield, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Litigation' },
  esg_event: { icon: Info, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'ESG Event' },
  market_hot: { icon: Flame, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Hot Market Signal' }
};

// Category configurations
const CATEGORY_CONFIG = {
  bullish: {
    label: 'Bullish',
    color: 'text-green-700',
    bg: 'bg-green-100',
    description: 'Increases acquisition likelihood'
  },
  bearish: {
    label: 'Bearish',
    color: 'text-red-700',
    bg: 'bg-red-100',
    description: 'Decreases acquisition likelihood'
  },
  neutral: {
    label: 'Neutral',
    color: 'text-gray-700',
    bg: 'bg-gray-100',
    description: 'Informational'
  }
};

// Appetite level configurations
const APPETITE_CONFIG = {
  very_high: { label: 'Very High', color: 'text-red-600', bg: 'bg-red-100' },
  high: { label: 'High', color: 'text-orange-600', bg: 'bg-orange-100' },
  medium: { label: 'Medium', color: 'text-yellow-600', bg: 'bg-yellow-100' },
  low: { label: 'Low', color: 'text-blue-600', bg: 'bg-blue-100' },
  inactive: { label: 'Inactive', color: 'text-gray-600', bg: 'bg-gray-100' }
};

// Signal card component
// API returns shortened field names (type, title, etc.) - we support both formats
function SignalCard({ signal }) {
  // Support both API formats: shortened (type, title) and full (signal_type, signal_title)
  const signalType = signal.type || signal.signal_type;
  const signalCategory = signal.category || signal.signal_category;
  const signalTitle = signal.title || signal.signal_title;
  const signalDescription = signal.description || signal.signal_description;
  const signalStrength = signal.strength || signal.signal_strength;
  const signalDate = signal.date || signal.signal_date;
  const sourceName = signal.source || signal.source_name;
  const sourceUrl = signal.source_url;
  const isVerified = signal.is_verified;

  const config = SIGNAL_TYPE_CONFIG[signalType] || SIGNAL_TYPE_CONFIG.manual_note;
  const categoryConfig = CATEGORY_CONFIG[signalCategory] || CATEGORY_CONFIG.neutral;
  const Icon = config.icon;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className={`p-4 rounded-lg border ${config.border} ${config.bg}`}>
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg bg-white shadow-sm`}>
          <Icon className={`w-5 h-5 ${config.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-full ${categoryConfig.bg} ${categoryConfig.color} font-medium`}>
              {categoryConfig.label}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full bg-white ${config.color} font-medium`}>
              {config.label}
            </span>
            {signalStrength >= 8 && (
              <span className="flex items-center gap-0.5 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                <Flame className="w-3 h-3" /> High Impact
              </span>
            )}
          </div>
          <h4 className="font-medium text-gray-900 mt-2">{signalTitle}</h4>
          {signalDescription && (
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{signalDescription}</p>
          )}
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(signalDate)}
            </span>
            {sourceName && (
              sourceUrl ? (
                <a
                  href={sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="w-3 h-3" />
                  {sourceName}
                </a>
              ) : (
                <span className="flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" />
                  {sourceName}
                </span>
              )
            )}
            {isVerified && (
              <span className="flex items-center gap-1 text-green-600">
                <CheckCircle2 className="w-3 h-3" />
                Verified
              </span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-900">{signalStrength}</div>
          <div className="text-xs text-gray-500">Strength</div>
        </div>
      </div>
    </div>
  );
}

// Pipeline gap component
function PipelineGap({ gap }) {
  const urgencyColors = {
    critical: 'bg-red-100 text-red-800 border-red-200',
    high: 'bg-orange-100 text-orange-800 border-orange-200',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    low: 'bg-green-100 text-green-800 border-green-200'
  };

  return (
    <div className={`p-3 rounded-lg border ${urgencyColors[gap.urgency] || urgencyColors.medium}`}>
      <div className="flex items-center justify-between">
        <span className="font-medium">{gap.area}</span>
        <span className="text-xs uppercase font-semibold">{gap.urgency}</span>
      </div>
      {gap.note && (
        <p className="text-sm mt-1 opacity-80">{gap.note}</p>
      )}
    </div>
  );
}

// Patent cliff component
function PatentCliff({ cliff }) {
  const yearsUntil = cliff.expiry_year - new Date().getFullYear();
  const urgencyColor = yearsUntil <= 2 ? 'text-red-600' : yearsUntil <= 4 ? 'text-orange-600' : 'text-yellow-600';

  return (
    <div className="p-3 rounded-lg border border-orange-200 bg-orange-50">
      <div className="flex items-center justify-between">
        <div>
          <span className="font-medium text-gray-900">{cliff.drug}</span>
          <div className="text-sm text-gray-600">${cliff.revenue_billions}B revenue at risk</div>
        </div>
        <div className="text-right">
          <div className={`text-lg font-bold ${urgencyColor}`}>{cliff.expiry_year}</div>
          <div className="text-xs text-gray-500">{yearsUntil} years</div>
        </div>
      </div>
    </div>
  );
}

// Intelligence Score Bar component
function ScoreBar({ label, score, icon: Icon, color, description }) {
  const getColorClasses = (score) => {
    if (score >= 70) return { bar: 'bg-green-500', text: 'text-green-600', bg: 'bg-green-50' };
    if (score >= 50) return { bar: 'bg-blue-500', text: 'text-blue-600', bg: 'bg-blue-50' };
    if (score >= 30) return { bar: 'bg-yellow-500', text: 'text-yellow-600', bg: 'bg-yellow-50' };
    return { bar: 'bg-gray-400', text: 'text-gray-600', bg: 'bg-gray-50' };
  };

  const colors = color || getColorClasses(score);

  return (
    <div className={`p-3 rounded-lg ${colors.bg} border border-gray-100`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-4 h-4 ${colors.text}`} />}
          <span className="text-sm font-medium text-gray-700">{label}</span>
        </div>
        <span className={`text-sm font-bold ${colors.text}`}>{score}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full ${colors.bar} transition-all duration-500`}
          style={{ width: `${Math.min(100, score)}%` }}
        />
      </div>
      {description && (
        <p className="text-xs text-gray-500 mt-1.5">{description}</p>
      )}
    </div>
  );
}

// Composite Intelligence Score component
function IntelligenceScoreCard({ intelligenceData }) {
  if (!intelligenceData) return null;

  const { compositeScore, recommendation, scores, topInsights, matchedGaps, dealStats } = intelligenceData;

  const getRecommendationConfig = (rec) => {
    const configs = {
      high_priority: { label: 'High Priority', color: 'text-green-700', bg: 'bg-green-100', border: 'border-green-200' },
      good_fit: { label: 'Good Fit', color: 'text-blue-700', bg: 'bg-blue-100', border: 'border-blue-200' },
      moderate: { label: 'Moderate', color: 'text-yellow-700', bg: 'bg-yellow-100', border: 'border-yellow-200' },
      low_priority: { label: 'Low Priority', color: 'text-gray-700', bg: 'bg-gray-100', border: 'border-gray-200' }
    };
    return configs[rec] || configs.moderate;
  };

  const recConfig = getRecommendationConfig(recommendation);

  return (
    <div className="space-y-4">
      {/* Composite Score Header */}
      <div className={`p-4 rounded-xl border-2 ${recConfig.border} ${recConfig.bg}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <PieChart className={`w-6 h-6 ${recConfig.color}`} />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Intelligence Score</h4>
              <span className={`text-sm font-medium ${recConfig.color}`}>
                {recConfig.label}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-3xl font-bold ${recConfig.color}`}>
              {compositeScore}
            </div>
            <div className="text-xs text-gray-500">/ 100</div>
          </div>
        </div>
      </div>

      {/* Component Scores Grid */}
      <div className="grid grid-cols-2 gap-3">
        <ScoreBar
          label="Financial Capacity"
          score={scores?.financialCapacity || 0}
          icon={DollarSign}
          description="Cash & deal capacity"
        />
        <ScoreBar
          label="Deal Pattern"
          score={scores?.dealPattern || 0}
          icon={BarChart3}
          description="M&A activity level"
        />
        <ScoreBar
          label="Portfolio Gap"
          score={scores?.portfolioGap || 0}
          icon={Target}
          description="Alignment with gaps"
        />
        <ScoreBar
          label="Earnings Mentions"
          score={scores?.earningsCallMention || 0}
          icon={Activity}
          description="BD focus signals"
        />
        <ScoreBar
          label="Competitive Pressure"
          score={scores?.competitivePressure || 0}
          icon={Shield}
          description="Market competition"
        />
      </div>

      {/* Top Insights */}
      {topInsights && topInsights.length > 0 && (
        <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-100">
          <h5 className="text-sm font-medium text-indigo-900 flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4" />
            Key Insights
          </h5>
          <ul className="space-y-1">
            {topInsights.slice(0, 5).map((insight, idx) => (
              <li key={idx} className="text-sm text-indigo-800 flex items-start gap-2">
                <span className="text-indigo-400 mt-0.5">•</span>
                {insight}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Deal Stats */}
      {dealStats && (dealStats.totalAcquisitions > 0 || dealStats.totalLicensing > 0) && (
        <div className="flex items-center gap-4 text-sm text-gray-600">
          {dealStats.totalAcquisitions > 0 && (
            <span className="flex items-center gap-1">
              <Building2 className="w-4 h-4 text-blue-500" />
              {dealStats.totalAcquisitions} acquisitions
            </span>
          )}
          {dealStats.totalLicensing > 0 && (
            <span className="flex items-center gap-1">
              <Briefcase className="w-4 h-4 text-purple-500" />
              {dealStats.totalLicensing} licensing deals
            </span>
          )}
          {dealStats.recentDeals > 0 && (
            <span className="flex items-center gap-1 text-green-600">
              <Flame className="w-4 h-4" />
              {dealStats.recentDeals} recent
            </span>
          )}
        </div>
      )}

      {/* Matched Gaps */}
      {matchedGaps && matchedGaps.length > 0 && (
        <div className="p-3 bg-orange-50 rounded-lg border border-orange-100">
          <h5 className="text-sm font-medium text-orange-900 flex items-center gap-2 mb-2">
            <Target className="w-4 h-4" />
            Pipeline Gaps Matching Your Area
          </h5>
          <div className="space-y-1">
            {matchedGaps.slice(0, 3).map((gap, idx) => (
              <div key={idx} className="text-sm text-orange-800 flex items-center justify-between">
                <span>{gap.area || gap.platform || 'Unknown'}</span>
                <span className="text-xs bg-orange-200 px-2 py-0.5 rounded">
                  Strength: {gap.strength}/10
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// BD Activity types for filtering
const BD_ACTIVITY_TYPES = [
  'acquisition_announced', 'acquisition_completed', 'acquisition_rumor',
  'licensing_in', 'licensing_out', 'partnership', 'option_exercise',
  'milestone_payment', 'china_bd_activity', 'geographic_expansion',
  'clinical_trial_start', 'fda_approval', 'pipeline_phase_advance',
  'divestiture', 'spin_off'
];

// BD Activity Timeline component
function BDActivityTimeline({ signals }) {
  // Filter to BD-related signals only
  const bdSignals = signals.filter(s => {
    const signalType = s.type || s.signal_type;
    return BD_ACTIVITY_TYPES.includes(signalType);
  }).slice(0, 6); // Show max 6 items

  if (bdSignals.length === 0) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const getActivityLabel = (type) => {
    const labels = {
      acquisition_announced: 'Acquisition',
      acquisition_completed: 'Acquired',
      acquisition_rumor: 'M&A Rumor',
      licensing_in: 'Licensed-In',
      licensing_out: 'Licensed-Out',
      partnership: 'Partnership',
      option_exercise: 'Option Exercise',
      milestone_payment: 'Milestone',
      china_bd_activity: 'China BD',
      geographic_expansion: 'Geo Expansion',
      clinical_trial_start: 'Trial Start',
      fda_approval: 'FDA Approval',
      pipeline_phase_advance: 'Phase Advance',
      divestiture: 'Divestiture',
      spin_off: 'Spin-Off'
    };
    return labels[type] || 'BD Activity';
  };

  const getActivityColor = (type) => {
    if (['acquisition_announced', 'acquisition_completed', 'licensing_in'].includes(type)) {
      return 'bg-green-100 text-green-700';
    }
    if (['licensing_out', 'divestiture', 'spin_off'].includes(type)) {
      return 'bg-amber-100 text-amber-700';
    }
    if (['clinical_trial_start', 'fda_approval', 'pipeline_phase_advance'].includes(type)) {
      return 'bg-blue-100 text-blue-700';
    }
    if (['china_bd_activity', 'geographic_expansion'].includes(type)) {
      return 'bg-rose-100 text-rose-700';
    }
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-4">
        <Briefcase className="w-4 h-4 text-indigo-500" />
        Recent BD Activity
      </h4>
      <div className="space-y-3">
        {bdSignals.map((signal, idx) => {
          const signalType = signal.type || signal.signal_type;
          const signalTitle = signal.title || signal.signal_title;
          const signalDate = signal.date || signal.signal_date;

          return (
            <div key={signal.id || idx} className="flex items-start gap-3">
              <div className="w-16 flex-shrink-0 text-xs text-gray-500 pt-0.5">
                {formatDate(signalDate)}
              </div>
              <div className="flex-1 flex items-start gap-2">
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${getActivityColor(signalType)}`}>
                  {getActivityLabel(signalType)}
                </span>
                <span className="text-sm text-gray-700 line-clamp-1">{signalTitle}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// What They're Looking For component
function WhatTheyLookingFor({ intelligence, signals }) {
  const intel = intelligence?.intelligence || {};
  const pipelineGaps = intel?.pipeline_gaps || [];

  // Analyze signals to determine what they're seeking
  const analyzeSeekingAreas = () => {
    const seeking = new Set();
    const recentFailures = [];
    const recentAcquisitions = [];

    signals.forEach(s => {
      const signalType = s.type || s.signal_type;
      const signalTitle = (s.title || s.signal_title || '').toLowerCase();
      const therapeuticArea = s.therapeutic_area;
      const modality = s.modality;

      // Track modalities/areas from licensing-in and acquisitions
      if (['licensing_in', 'acquisition_announced', 'acquisition_completed'].includes(signalType)) {
        if (modality) seeking.add(modality);
        if (therapeuticArea) seeking.add(therapeuticArea);
        recentAcquisitions.push(s.title || s.signal_title);
      }

      // Track failures
      if (['pipeline_failure', 'clinical_hold'].includes(signalType)) {
        recentFailures.push(s.title || s.signal_title);
        if (therapeuticArea) seeking.add(therapeuticArea + ' (gap)');
      }
    });

    // Add from pipeline gaps
    pipelineGaps.forEach(gap => {
      if (gap.area) seeking.add(gap.area);
      if (gap.platform) seeking.add(gap.platform);
    });

    return {
      seeking: Array.from(seeking).slice(0, 5),
      recentFailures: recentFailures.slice(0, 2),
      recentAcquisitions: recentAcquisitions.slice(0, 2)
    };
  };

  const { seeking, recentFailures } = analyzeSeekingAreas();

  // Determine deal capacity
  const getDealCapacity = () => {
    const cash = intel?.cash_position_billions;
    if (!cash) return null;
    if (cash >= 50) return { label: '$50B+ (very strong)', color: 'text-green-600' };
    if (cash >= 20) return { label: `$${cash}B (strong)`, color: 'text-green-600' };
    if (cash >= 10) return { label: `$${cash}B (moderate)`, color: 'text-blue-600' };
    return { label: `$${cash}B (limited)`, color: 'text-gray-600' };
  };

  // Determine recent focus from signals
  const getRecentFocus = () => {
    const focuses = [];
    const hasChina = signals.some(s => (s.type || s.signal_type) === 'china_bd_activity');
    const hasLicensing = signals.some(s => ['licensing_in', 'licensing_out'].includes(s.type || s.signal_type));
    const hasAcquisition = signals.some(s => ['acquisition_announced', 'acquisition_completed'].includes(s.type || s.signal_type));

    if (hasChina) focuses.push('China/Asia deals');
    if (hasLicensing) focuses.push('Licensing');
    if (hasAcquisition) focuses.push('M&A');

    return focuses.length > 0 ? focuses.join(', ') : null;
  };

  const dealCapacity = getDealCapacity();
  const recentFocus = getRecentFocus();

  // Don't show if no meaningful data
  if (seeking.length === 0 && !dealCapacity && !recentFocus && recentFailures.length === 0) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-4">
      <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-4">
        <Target className="w-4 h-4 text-indigo-600" />
        What They're Looking For
      </h4>
      <div className="space-y-3">
        {seeking.length > 0 && (
          <div className="flex items-start gap-3">
            <span className="text-sm text-gray-500 w-28 flex-shrink-0">Actively seeking:</span>
            <div className="flex flex-wrap gap-1.5">
              {seeking.map((item, idx) => (
                <span key={idx} className="px-2 py-0.5 bg-white text-indigo-700 text-xs font-medium rounded border border-indigo-200">
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}

        {recentFailures.length > 0 && (
          <div className="flex items-start gap-3">
            <span className="text-sm text-gray-500 w-28 flex-shrink-0">Gap to fill:</span>
            <span className="text-sm text-red-600 font-medium">
              {recentFailures[0]} {recentFailures.length > 1 && `(+${recentFailures.length - 1} more)`}
            </span>
          </div>
        )}

        {dealCapacity && (
          <div className="flex items-start gap-3">
            <span className="text-sm text-gray-500 w-28 flex-shrink-0">Deal capacity:</span>
            <span className={`text-sm font-medium ${dealCapacity.color}`}>
              {dealCapacity.label}
            </span>
          </div>
        )}

        {recentFocus && (
          <div className="flex items-start gap-3">
            <span className="text-sm text-gray-500 w-28 flex-shrink-0">Recent focus:</span>
            <span className="text-sm text-gray-700 font-medium">{recentFocus}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper function to group signals by time period
function groupSignalsByTime(signals) {
  const now = new Date();
  const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
  const threeMonthsAgo = new Date(now - 90 * 24 * 60 * 60 * 1000);

  const groups = {
    recent: { label: 'Last 30 Days', signals: [], defaultExpanded: true },
    midterm: { label: '1-3 Months Ago', signals: [], defaultExpanded: false },
    older: { label: '3-12 Months Ago', signals: [], defaultExpanded: false }
  };

  signals.forEach(signal => {
    const signalDate = new Date(signal.date || signal.signal_date);
    if (signalDate >= thirtyDaysAgo) {
      groups.recent.signals.push(signal);
    } else if (signalDate >= threeMonthsAgo) {
      groups.midterm.signals.push(signal);
    } else {
      groups.older.signals.push(signal);
    }
  });

  return groups;
}

// Signal Time Group component
function SignalTimeGroup({ groupKey, label, signals, defaultExpanded, getCategoryCount }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (signals.length === 0) return null;

  const bullishCount = signals.filter(s => (s.category || s.signal_category) === 'bullish').length;
  const bearishCount = signals.filter(s => (s.category || s.signal_category) === 'bearish').length;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full px-4 py-3 flex items-center justify-between text-left transition-colors ${
          expanded ? 'bg-gray-50' : 'bg-white hover:bg-gray-50'
        }`}
      >
        <div className="flex items-center gap-3">
          <span className={`font-medium ${defaultExpanded ? 'text-gray-900' : 'text-gray-700'}`}>
            {label}
          </span>
          <span className="text-sm text-gray-500">({signals.length} signals)</span>
          {bullishCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
              <TrendingUp className="w-3 h-3" />
              {bullishCount}
            </span>
          )}
          {bearishCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
              <TrendingDown className="w-3 h-3" />
              {bearishCount}
            </span>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
      </button>
      {expanded && (
        <div className="p-4 space-y-3 bg-white border-t border-gray-100">
          {signals.map((signal) => (
            <SignalCard key={signal.id} signal={signal} />
          ))}
        </div>
      )}
    </div>
  );
}

// Main component
export function BuyerIntelligence({ buyerId, compact = false }) {
  const [intelligence, setIntelligence] = useState(null);
  const [intelligenceScores, setIntelligenceScores] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllGaps, setShowAllGaps] = useState(false);
  const [showIntelligenceScores, setShowIntelligenceScores] = useState(true);

  useEffect(() => {
    if (!buyerId) return;

    const fetchIntelligence = async () => {
      try {
        setLoading(true);

        // Fetch both intelligence data and comprehensive scores in parallel
        const [intelligenceResponse, scoresResponse] = await Promise.all([
          buyersAPI.getIntelligence(buyerId),
          matchingAPI.getIntelligence(buyerId).catch(err => {
            console.warn('Failed to fetch intelligence scores:', err);
            return null;
          })
        ]);

        if (intelligenceResponse.data?.success) {
          setIntelligence(intelligenceResponse.data.data);
        }

        if (scoresResponse?.data?.success) {
          setIntelligenceScores(scoresResponse.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch buyer intelligence:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchIntelligence();
  }, [buyerId]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-24 bg-gray-100 rounded-lg" />
        <div className="h-32 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        Failed to load buyer intelligence
      </div>
    );
  }

  if (!intelligence) {
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-gray-600">
        No intelligence data available for this buyer
      </div>
    );
  }

  const intel = intelligence.intelligence;
  const signals = intelligence.recent_signals || [];
  const pipelineGaps = intel?.pipeline_gaps || [];
  const patentCliffs = intel?.patent_cliffs || [];
  const appetiteConfig = APPETITE_CONFIG[intel?.acquisition_appetite] || APPETITE_CONFIG.medium;

  // Compact version for cards
  if (compact) {
    // Support both API formats for category field
    const bullishCount = signals.filter(s => (s.category || s.signal_category) === 'bullish').length;
    const bearishCount = signals.filter(s => (s.category || s.signal_category) === 'bearish').length;

    // Get first signal's type and title (support both formats)
    const firstSignal = signals[0];
    const firstSignalType = firstSignal?.type || firstSignal?.signal_type;
    const firstSignalTitle = firstSignal?.title || firstSignal?.signal_title;

    // Get recommendation color based on composite score
    const getScoreColor = (score) => {
      if (score >= 70) return 'text-green-600 bg-green-50';
      if (score >= 50) return 'text-blue-600 bg-blue-50';
      if (score >= 30) return 'text-yellow-600 bg-yellow-50';
      return 'text-gray-600 bg-gray-50';
    };

    return (
      <div className="space-y-3">
        {/* Intelligence Score (if available) + Appetite indicator */}
        <div className="flex items-center justify-between">
          {intelligenceScores?.compositeScore ? (
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 rounded text-sm font-semibold ${getScoreColor(intelligenceScores.compositeScore)}`}>
                {intelligenceScores.compositeScore}
              </span>
              <span className="text-xs text-gray-500">Intel Score</span>
            </div>
          ) : (
            <span className="text-sm text-gray-600">Acquisition Appetite</span>
          )}
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${appetiteConfig.bg} ${appetiteConfig.color}`}>
            {appetiteConfig.label}
          </span>
        </div>

        {/* Quick stats */}
        <div className="flex items-center gap-4 text-sm">
          {bullishCount > 0 && (
            <span className="flex items-center gap-1 text-green-600">
              <TrendingUp className="w-4 h-4" />
              {bullishCount} bullish
            </span>
          )}
          {bearishCount > 0 && (
            <span className="flex items-center gap-1 text-red-600">
              <TrendingDown className="w-4 h-4" />
              {bearishCount} bearish
            </span>
          )}
          {pipelineGaps.length > 0 && (
            <span className="flex items-center gap-1 text-orange-600">
              <Target className="w-4 h-4" />
              {pipelineGaps.length} gaps
            </span>
          )}
        </div>

        {/* Top insight from intelligence (if available) */}
        {intelligenceScores?.topInsights?.[0] && (
          <div className="p-2 rounded text-xs bg-indigo-50 text-indigo-700">
            <span className="font-medium">{intelligenceScores.topInsights[0]}</span>
          </div>
        )}

        {/* Top signal preview (fallback if no insights) */}
        {!intelligenceScores?.topInsights?.[0] && signals.length > 0 && firstSignalTitle && (
          <div className={`p-2 rounded text-xs ${SIGNAL_TYPE_CONFIG[firstSignalType]?.bg || 'bg-gray-50'}`}>
            <span className="font-medium">{firstSignalTitle}</span>
          </div>
        )}
      </div>
    );
  }

  // Full version
  return (
    <div className="space-y-6">
      {/* Header with score */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
        <div>
          <h3 className="font-semibold text-gray-900">Buyer Intelligence</h3>
          <p className="text-sm text-gray-600 mt-0.5">{intelligence.company_name}</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-indigo-600">
            {intelligence.acquisition_appetite_score || 50}
          </div>
          <div className="text-xs text-gray-500">Appetite Score</div>
        </div>
      </div>

      {/* Intelligence Scores Section */}
      {intelligenceScores && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-indigo-500" />
              Intelligence Analysis
            </h4>
            <button
              onClick={() => setShowIntelligenceScores(!showIntelligenceScores)}
              className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
            >
              {showIntelligenceScores ? 'Hide' : 'Show'}
              {showIntelligenceScores ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
          {showIntelligenceScores && (
            <IntelligenceScoreCard intelligenceData={intelligenceScores} />
          )}
        </div>
      )}

      {/* What They're Looking For - Key Summary */}
      <WhatTheyLookingFor intelligence={intelligence} signals={signals} />

      {/* Recent BD Activity Timeline */}
      <BDActivityTimeline signals={signals} />

      {/* Investment thesis */}
      {intel?.investment_thesis && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Investment Thesis
          </h4>
          <p className="text-sm text-blue-800 mt-2">{intel.investment_thesis}</p>
        </div>
      )}

      {/* Key metrics */}
      {intel && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-3 bg-gray-50 rounded-lg text-center">
            <div className={`text-sm font-medium ${appetiteConfig.color}`}>
              {appetiteConfig.label}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">Acquisition Appetite</div>
          </div>
          {intel.cash_position_billions && (
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-sm font-medium text-gray-900">
                ${intel.cash_position_billions}B
              </div>
              <div className="text-xs text-gray-500 mt-0.5">Cash Position</div>
            </div>
          )}
          {intel.revenue_at_risk_billions && (
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-sm font-medium text-orange-600">
                ${intel.revenue_at_risk_billions}B
              </div>
              <div className="text-xs text-gray-500 mt-0.5">Revenue at Risk</div>
            </div>
          )}
          {intel.financial_capacity && (
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-sm font-medium text-gray-900 capitalize">
                {intel.financial_capacity}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">Deal Capacity</div>
            </div>
          )}
        </div>
      )}

      {/* Pipeline Gaps */}
      {pipelineGaps.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900 flex items-center gap-2">
              <Target className="w-4 h-4 text-orange-500" />
              Pipeline Gaps
            </h4>
            {pipelineGaps.length > 3 && (
              <button
                onClick={() => setShowAllGaps(!showAllGaps)}
                className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
              >
                {showAllGaps ? 'Show less' : `Show all ${pipelineGaps.length}`}
                {showAllGaps ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(showAllGaps ? pipelineGaps : pipelineGaps.slice(0, 4)).map((gap, index) => (
              <PipelineGap key={index} gap={gap} />
            ))}
          </div>
        </div>
      )}

      {/* Patent Cliffs */}
      {patentCliffs.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-orange-500" />
            Patent Cliffs
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {patentCliffs.slice(0, 4).map((cliff, index) => (
              <PatentCliff key={index} cliff={cliff} />
            ))}
          </div>
        </div>
      )}

      {/* Signals - Grouped by Time Period */}
      {signals.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-indigo-500" />
            Buyer Signals ({signals.length} total)
          </h4>
          {(() => {
            const groups = groupSignalsByTime(signals);
            return (
              <div className="space-y-3">
                <SignalTimeGroup
                  groupKey="recent"
                  label={groups.recent.label}
                  signals={groups.recent.signals}
                  defaultExpanded={true}
                />
                <SignalTimeGroup
                  groupKey="midterm"
                  label={groups.midterm.label}
                  signals={groups.midterm.signals}
                  defaultExpanded={false}
                />
                <SignalTimeGroup
                  groupKey="older"
                  label={groups.older.label}
                  signals={groups.older.signals}
                  defaultExpanded={false}
                />
              </div>
            );
          })()}
        </div>
      )}

      {/* Data quality indicator */}
      {intel?.last_verified_at && (
        <div className="flex items-center justify-between text-xs text-gray-500 pt-4 border-t border-gray-100">
          <span>Last verified: {new Date(intel.last_verified_at).toLocaleDateString()}</span>
          <span className="flex items-center gap-1">
            Data quality: {intel.data_quality_score || 50}%
          </span>
        </div>
      )}
    </div>
  );
}

export default BuyerIntelligence;
